/**
 * SSO Login Page
 * Handles SSO authentication from One Manager Platform
 *
 * URL: /sso?token=<sso_token>
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Use the base API URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default function SsoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Authenticating...');

  useEffect(() => {
    const ssoToken = searchParams.get('token');

    if (!ssoToken) {
      setError('No SSO token provided');
      return;
    }

    handleSsoLogin(ssoToken);
  }, [searchParams]);

  const handleSsoLogin = async (ssoToken) => {
    try {
      setStatus('Validating SSO token...');

      // Call BMS SSO login endpoint directly (not through the API client which may add interceptors)
      const response = await fetch(`${API_URL}/sso/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ssoToken })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('Login successful! Redirecting...');

        // Build user object compatible with BMS
        const user = {
          id: data.user.id,
          email: data.user.email,
          role: data.user.role,
          full_name: data.user.full_name || data.user.email,
          tenantRole: data.user.tenantRole,
          modules: data.user.modules || {},
          moduleConfig: data.user.moduleConfig || {},
          limits: data.user.limits || {},
          is_sso: true
        };

        // Use ssoLogin from AuthContext
        ssoLogin(data.accessToken, user, data.tenant);

        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1000);
      } else {
        setError(data.error || 'SSO login failed');
      }
    } catch (err) {
      console.error('SSO login error:', err);
      setError(err.message || 'SSO authentication failed');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">SSO Login Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Login
            </button>
            <button
              onClick={() => window.close()}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <LoadingSpinner />
        <h2 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Single Sign-On</h2>
        <p className="text-gray-600">{status}</p>
        <p className="text-sm text-gray-400 mt-4">Authenticating via One Manager Platform...</p>
      </div>
    </div>
  );
}
